import { useState } from "react";
import { Link, useOutletContext } from "react-router";
import { useSupplyPoints } from "../api/queries.js";
import type { ShellContext } from "../shell/Shell.js";
import { number } from "./imports-helpers.js";
import {
  bandOf,
  confidenceColour,
  lastActualLabel,
  readMethodLabel,
  type ConfidenceBand,
} from "./registry-helpers.js";

/** The confidence meter as the prototype draws it: a bar and the number. */
export function ConfidenceMeter(props: { score: number }) {
  return (
    <span className="meter" aria-label={`Confidence ${props.score}`}>
      <span className="bar">
        <i style={{ width: `${props.score}%`, background: confidenceColour(props.score) }} />
      </span>
      <span className="mono" style={{ fontSize: ".76rem" }}>
        {props.score}
      </span>
    </span>
  );
}

/** Data Assurance · Registry · Supply points (REG-06, AC-1-06): filters, the count, the table. */
export function SupplyPoints() {
  const { client } = useOutletContext<ShellContext>();
  const [q, setQ] = useState("");
  const [readMethod, setReadMethod] = useState<"" | "smart" | "amr" | "manual">("");
  const [band, setBand] = useState<ConfidenceBand>("");
  const range = bandOf(band);
  const page = useSupplyPoints(client, {
    q: q || undefined,
    readMethod: readMethod || undefined,
    minConfidence: range.min,
    maxConfidence: range.max,
  });
  const items = page.data?.items ?? [];

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Data Assurance · Registry</div>
          <h1>Supply points</h1>
        </div>
        <span className="muted mono" style={{ fontSize: ".76rem" }} aria-live="polite">
          {page.data ? `Showing ${number(items.length)} of ${number(page.data.total)}` : "Loading"}
        </span>
      </div>
      <section className="card">
        <header>
          <div className="filters">
            <input
              className="input"
              placeholder="SPID, name, address or postcode"
              aria-label="Filter supply points"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select
              aria-label="Read method"
              value={readMethod}
              onChange={(e) => setReadMethod(e.target.value as typeof readMethod)}
            >
              <option value="">All read methods</option>
              <option value="smart">Smart</option>
              <option value="amr">AMR</option>
              <option value="manual">Manual</option>
            </select>
            <select
              aria-label="Confidence"
              value={band}
              onChange={(e) => setBand(e.target.value as ConfidenceBand)}
            >
              <option value="">All confidence</option>
              <option value="low">Below 65</option>
              <option value="mid">65 to 84</option>
              <option value="high">85 and above</option>
            </select>
          </div>
        </header>
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>SPID</th>
                <th>Premises</th>
                <th>Retailer</th>
                <th>Reads</th>
                <th>Last actual</th>
                <th>Confidence</th>
                <th>Findings</th>
              </tr>
            </thead>
            <tbody>
              {page.isPending ? (
                [0, 1, 2, 3].map((i) => (
                  <tr key={i}>
                    <td colSpan={7}>
                      <div className="skeleton" />
                    </td>
                  </tr>
                ))
              ) : page.isError ? (
                <tr>
                  <td colSpan={7} className="empty">
                    The registry could not be loaded: {page.error.message}. Try again in a moment.
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty">
                    {page.data && page.data.total === 0 && !q && !readMethod && !band
                      ? "No supply points yet. They appear once an extract has been committed through Imports."
                      : "No supply points match these filters."}
                  </td>
                </tr>
              ) : (
                items.map((p) => {
                  const last = lastActualLabel(p.lastActualReadAt);
                  const [name, ...rest] = p.address.split(", ");
                  return (
                    <tr key={p.id} className="click">
                      <td className="mono">
                        <Link
                          to={`/supply-points/${p.spid}`}
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          {p.spid}
                        </Link>
                      </td>
                      <td>
                        {name}
                        <br />
                        <span className="faint" style={{ fontSize: ".76rem" }}>
                          {rest.join(", ")}
                        </span>
                      </td>
                      <td>{p.retailerName}</td>
                      <td>{readMethodLabel(p.readMethod)}</td>
                      <td
                        className="mono"
                        style={{
                          fontSize: ".78rem",
                          ...(last.overdue ? { color: "var(--crit)" } : {}),
                        }}
                      >
                        {last.label}
                      </td>
                      <td>
                        <ConfidenceMeter score={p.confidenceScore} />
                      </td>
                      <td>
                        {p.openFindings > 0 ? (
                          <span
                            className={`pill ${p.topSeverity === "critical" ? "crit" : p.topSeverity === "high" ? "warn" : "vio"}`}
                          >
                            {p.openFindings} open
                          </span>
                        ) : (
                          <span className="pill ok">Clean</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
